import {useState} from "react";
import {adjustFromTimeZoneSafely, DriverActivityEntity} from "@ots-frontend/ots-data";
import {ColDef, ColGroupDef} from "@ag-grid-community/core";
import {GeofenceEventCell} from "./cells/GeofenceEventCell/GeofenceEventCell";
import {getFormattedDateTime} from "../date/FormattedDateTime/FormattedDateTime";
import {DateCell} from "./cells/DateCell/DateCell";
import {PersonToggleCell} from "./cells/PersonToggleCell/PersonToggleCell";
import {ValidationCell} from "./cells/ValidationCell/ValidationCell";
import {CommentCell} from "./cells/CommentCell/CommentCell";
import {IdCell} from "./cells/IdCell/IdCell";
import {LocationCell} from "./cells/LocationCell/LocationCell";
import {DVIRCell} from "./cells/DVIRCell/DVIRCell";

export type UseDriverActivityColsProps = {}

export type UseDriverActivityColsResult = {
  colDefs: (ColDef<DriverActivityEntity> | ColGroupDef<DriverActivityEntity>)[]
  setColDefs: (colDefs: (ColDef<DriverActivityEntity> | ColGroupDef<DriverActivityEntity>)[]) => void
}

export const useDriverActivityCols = (props: UseDriverActivityColsProps): UseDriverActivityColsResult => {
  const [_colDefs, _setColDefs] = useState<(ColDef<DriverActivityEntity> | ColGroupDef<DriverActivityEntity>)[]>([
    // {
    //   headerName: "",
    //   width: 48,
    //   resizable: false,
    //   suppressMovable: true,
    //   sortable: false,
    //   valueGetter: params => {
    //     return params?.data?.warnings && params.data.warnings.length > 0
    //   },
    //   cellRendererSelector: params => {
    //     return {
    //       component: WarningCell
    //     }
    //   },
    // },
    {
      headerName: "",
      width: 75,
      resizable: false,
      suppressMovable: true,
      sortable: false,
      valueGetter: params => {
        const tta = params.data?.tta
        if (tta) {
          return typeof tta.dispatchValidatedDttm !== "undefined"
        }

        return undefined
      },
      cellRendererSelector: params => {
        return {
          component: ValidationCell
        }
      },
    },
    {
      headerName: "Pickup",
      children: [
        {
          colId: "pickupGeofenceEvent",
          headerName: "",
          valueGetter: params => {
            const data = params.data
            if (data) {
              if (data.person) {
                return data.hidden ?? false
              } else if (data.tta) {
                return {
                  geofenceEvent: data.tta.pickupTransport?.geofenceEvent,
                  skipGeofenceEvent: data.tta.pickupTransport?.skipGeofenceEvent,
                }
              } else if (data.pickupTransport) {
                return {
                  geofenceEvent: data.pickupTransport.geofenceEvent,
                  skipGeofenceEvent: data.pickupTransport.skipGeofenceEvent,
                }
              } else if (data.startShift) {
                return undefined
              }
            }

            return undefined
          },
          resizable: false,
          width: 48,
          cellRendererSelector: params => {
            const data = params.data
            const type = data?.type
            if (type === "tta" || type === "pickupTransport") {
              return {
                component: GeofenceEventCell,
              }
            } else if (type === "person") {
              return {
                component: PersonToggleCell
              }
            } else if (type === "startShift") {
              return {
                component: DVIRCell
              }
            }
          },
          suppressMovable: true,
          sortable: false,
        },
        {
          headerName: "Ticket #",
          valueGetter: params => {
            const data = params.data
            const type = data?.type
            if (type === "person") {
              return data?.person?.nameReverse
            } else if (type === "startShift") {
              if (data?.startShift?.startDttm) {
                return "Start Shift - " + getFormattedDateTime(data.startShift.startDttm)
              }
            } else if (type === "endShift") {
              if (data?.endShift?.endDttm) {
                return "End Shift - " + getFormattedDateTime(data.endShift.endDttm)
              }
            } else if (type === "tta") {
              return data?.tta?.pickupTransport?.ticketNumber
            } else if (type === "pickupTransport") {
              return data?.pickupTransport?.ticketNumber
            } else if (type === "geofenceEvent") {
              return data?.geofenceEvent?.outDttm ? "Entry/Exit" : "Entry"
            } else if (type === "dispatch") {
              return "Dispatch"
            }

            return undefined
          },
          colSpan: (params) => params?.data?.type !== "person" && params?.data?.type !== "startShift" && params?.data?.type !== "endShift" ? 1 : 4,
          resizable: false,
          width: 95,
          suppressMovable: true,
          sortable: false,
        },
        {
          headerName: "Date",
          valueGetter: params => {
            const data = params.data
            const type = data?.type
            if (type === "tta") {
              return adjustFromTimeZoneSafely(data?.tta?.pickupTransport?.createDttm)
            } else if (type === "pickupTransport") {
              return adjustFromTimeZoneSafely(data?.pickupTransport?.createDttm)
            } else if (type === "geofenceEvent") {
              return adjustFromTimeZoneSafely(data?.geofenceEvent?.inDttm)
            } else if (type === "dispatch") {
              return adjustFromTimeZoneSafely(data?.dispatch?.scheduleDttm)
            }

            return undefined
          },
          cellRendererSelector: params => {
            return {
              component: DateCell
            }
          },
          resizable: false,
          width: 145,
          suppressMovable: true,
          sortable: false,
        },
        {
          colId: "pickupSite",
          headerName: "Site",
          valueGetter: params => {
            const data = params.data
            const type = data?.type
            if (type === "tta" || type === "pickupTransport") {
              const transport = data?.tta?.pickupTransport ?? data?.pickupTransport
              return {
                name: transport?.siteName,
                nonDefaultStage: transport?.wellStageIsDefault,
                restrictions: transport?.restrictions,
              }
            } else if (type === "geofenceEvent") {
              return {
                name: data?.geofenceEvent?.siteName,
              }
            } else if (type === "dispatch") {
              return {
                name: data?.dispatch?.pickupSiteName,
              }
            }

            return undefined
          },
          cellRendererSelector: params => {
            return {
              component: LocationCell
            }
          },
          minWidth: 250,
          resizable: true,
          wrapText: true,
          autoHeight: true,
          suppressMovable: true,
          sortable: false,
        },
        {
          headerName: "Qty",
          valueGetter: params => {
            const data = params.data
            const type = data?.type
            if (type === "tta") {
              return data?.tta?.pickupTransport?.quantityDecimal
            } else if (type === "pickupTransport") {
              return data?.pickupTransport?.quantityDecimal
            } else if (type === "dispatch") {
              return data?.dispatch?.quantity
            }

            return undefined
          },
          resizable: false,
          width: 78,
          suppressMovable: true,
          sortable: false,
        },
      ]
    },
    {
      headerName: "Delivery",
      headerClass: 'dad-cell-divider',
      children: [
        {
          colId: "deliveryGeofenceEvent",
          headerName: "",
          valueGetter: params => {
            const data = params.data
            const type = data?.type
            if (type === "tta") {
              return {
                geofenceEvent: data.tta.deliveryTransport?.geofenceEvent,
                skipGeofenceEvent: data.tta.deliveryTransport?.skipGeofenceEvent,
              }
            } else if (type === "deliveryTransport") {
              return {
                geofenceEvent: data?.deliveryTransport?.geofenceEvent,
                skipGeofenceEvent: data?.deliveryTransport?.skipGeofenceEvent,
              }
            }

            return undefined
          },
          resizable: false,
          width: 48,
          cellRendererSelector: params => {
            return {
              component: GeofenceEventCell,
            }
          },
          headerClass: "dad-cell-divider",
          cellClass: "dad-cell-divider",
          suppressMovable: true,
          sortable: false,
        },
        {
          headerName: "Ticket #",
          valueGetter: params => {
            const data = params.data
            const type = data?.type
            if (type === "tta") {
              return data?.tta?.deliveryTransport?.ticketNumber
            } else if (type === "deliveryTransport") {
              return data?.deliveryTransport?.ticketNumber
            }

            return undefined
          },
          resizable: false,
          width: 95,
          suppressMovable: true,
          sortable: false,
        },
        {
          headerName: "Date",
          valueGetter: params => {
            const data = params.data
            const type = data?.type
            if (type === "tta") {
              return adjustFromTimeZoneSafely(data?.tta?.deliveryTransport?.createDttm)
            } else if (type === "deliveryTransport") {
              return adjustFromTimeZoneSafely(data?.deliveryTransport?.createDttm)
            } else if (type === "geofenceEvent") {
              return adjustFromTimeZoneSafely(data?.geofenceEvent?.outDttm)
            }

            return undefined
          },
          cellRendererSelector: params => {
            return {
              component: DateCell
            }
          },
          resizable: false,
          width: 145,
          suppressMovable: true,
          sortable: false,
        },
        {
          colId: "deliverySite",
          headerName: "Site",
          valueGetter: params => {
            const data = params.data
            const type = data?.type
            if (type === "tta" || type === "deliveryTransport") {
              const transport = data?.tta?.deliveryTransport ?? data?.deliveryTransport

              let name = transport?.siteName
              if (data?.tta && name && data.tta.deliveryMultipartRecord) {
                name += " (Split-Load)"
              }

              return {
                name: name,
                nonDefaultStage: transport?.wellStageIsDefault,
                restrictions: transport?.restrictions,
              }
            } else if (type === "dispatch") {
              return {
                name: data?.dispatch?.deliverySiteName,
              }
            }

            return undefined
          },
          cellRendererSelector: params => {
            return {
              component: LocationCell
            }
          },
          minWidth: 250,
          resizable: true,
          suppressMovable: true,
          sortable: false,
          wrapText: true,
          autoHeight: true,
        },
        {
          headerName: "Qty",
          valueGetter: params => {
            const data = params.data
            const type = data?.type
            if (type === "tta") {
              return data?.tta?.deliveryTransport?.quantityDecimal
            } else if (type === "deliveryTransport") {
              return data?.deliveryTransport?.quantityDecimal
            }
            return undefined
          },
          resizable: false,
          width: 78,
          suppressMovable: true,
          sortable: false,
        },
      ],
    },
    {
      headerName: "Details",
      headerClass: "dad-cell-divider",
      children: [
        {
          headerName: "Freight",
          valueGetter: params => {
            const data = params.data
            const type = data?.type
            if (type === "tta") {
              return data?.tta?.pickupTransport?.waterTypeName
            } else if (type === "pickupTransport") {
              return data?.pickupTransport?.waterTypeName
            } else if (type === "deliveryTransport") {
              return data?.deliveryTransport?.waterTypeName
            } else if (type === "dispatch") {
              return data?.dispatch?.freightTypeName
            }
            return undefined
          },
          headerClass: "dad-cell-divider",
          cellClass: "dad-cell-divider",
          resizable: false,
          width: 100,
          suppressMovable: true,
          sortable: false,
        },
        {
          headerName: "Work",
          valueGetter: params => {
            const data = params.data
            const type = data?.type
            if (type === "tta") {
              return data?.tta?.pickupTransport?.serviceTypeName ?? "Normal"
            } else if (type === "pickupTransport") {
              return data?.pickupTransport?.serviceTypeName ?? "Normal"
            } else if (type === "deliveryTransport") {
              return data?.deliveryTransport?.serviceTypeName ?? "Normal"
            } else if (type === "dispatch") {
              return data?.dispatch?.pickupServiceTypeName ?? "Normal"
            }
            return undefined
          },
          resizable: false,
          width: 92,
          suppressMovable: true,
          sortable: false,
        },
        {
          headerName: "ID",
          valueGetter: params => {
            const data = params.data
            const type = data?.type
            if (type === "tta") {
              return {
                id: data?.tta?.resourceIdentifiers,
                dispatchMatch: data?.tta?.dispatches && data.tta.dispatches.length > 0,
                rrcEp10WasteManifestBuildStatus: data?.tta?.rrcEp10WasteManifestBuildStatus,
              }
            } else if (type === "pickupTransport") {
              return {
                id: "",
                dispatchMatch: false,
                rrcEp10WasteManifestBuildStatus: data?.pickupTransport?.rrcEp10WasteManifestBuildStatus,
              }
            }
            return undefined
          },
          cellRendererSelector: params => {
            return {
              component: IdCell
            }
          },
          resizable: false,
          width: 138,
          suppressMovable: true,
          sortable: false,
        },
        {
          colId: "extras",
          headerName: "",
          width: 48,
          resizable: false,
          suppressMovable: true,
          sortable: false,
          valueGetter: params => {
            let extras = false
            const data = params.data
            if (data) {
              if (data.tta) {
                if (data.tta.pickupTransport && data.tta.pickupTransport.messages) {
                  extras = data.tta.pickupTransport.messages.length > 0
                }
                if (!extras && data.tta.pickupTransport && data.tta.pickupTransport.supplementals) {
                  extras = data.tta.pickupTransport.supplementals.length > 0
                }
                if (!extras && data.tta.deliveryTransport && data.tta.deliveryTransport.messages) {
                  extras = data.tta.deliveryTransport.messages.length > 0
                }
                if (!extras && data.tta.deliveryTransport && data.tta.deliveryTransport.supplementals) {
                  extras = data.tta.deliveryTransport.supplementals.length > 0
                }
              } else if (data.transport) {
                if (data.transport.messages) {
                  extras = data.transport.messages.length > 0
                }
                if (!extras && data.transport.supplementals) {
                  extras = data.transport.supplementals.length > 0
                }
              } else if (data.shift) {
                extras = data.shift.messages.length > 0
              }
            }
            return extras
          },
          cellRendererSelector: params => {
            return {
              component: CommentCell
            }
          },
        },
      ]
    },
  ])

  return {
    colDefs: _colDefs,
    setColDefs: _setColDefs,
  };
}
